<script setup lang="ts">
import { computed } from 'vue';
import { SCHEDULES_UPDATING_TEXT, isIndexedSchedule } from '@/components/arrayColumns/scheduleCell';
import { Checkbox } from '@/components/ui/checkbox';
import { requestPlaylistContainerSection } from '@/composables/playlistContainerSectionRequest';
import PlaylistContainerZoneLine from '@/components/DataTable/PlaylistContainerZoneLine.vue';
import { zoneLineRowFor } from './zoneLineRow';
import { scheduleNow } from '@/composables/useScheduleClock';
import ScheduleRow from './ScheduleRow.vue';
import ScheduleRowActions from './ScheduleRowActions.vue';
import { positiveZoneId, type ScheduleEntry } from './scheduleGroups';
import { useCardSchedules } from './useCardSchedules';

/**
 * The schedules on a custom-list search card, one line each.
 *
 * Each line is drawn with the same card the inspector's Schedules view uses
 * ({@link ScheduleRow}). It is embedded, so it has no border of its own and no
 * warning lines. The lines are the schedules {@link useCardSchedules} returns:
 * every schedule on the list when the search had no schedule rules, or only the
 * ones that passed them. For example, a filter for schedules starting Nov 1–7
 * draws 2 lines on a list of 12 schedules. The card does not say how many
 * schedules it leaves out, because the schedule counts in the card's heading
 * already count every schedule.
 *
 * A click on a line opens the list's inspector on Schedules, with that schedule
 * highlighted. The line leaves a section request, and the click carries on to
 * the card, which opens the list.
 *
 * With the pointer over a line, Edit and Delete show beside it, the same buttons
 * the inspector's Schedules section has. When the page makes schedules
 * selectable, a checkbox shows before the line too. It shows only while the
 * pointer is over that line, or once it is ticked. The search card hides the
 * list's own checkbox while the pointer is over any schedule line, so only one
 * checkbox shows at a time.
 */
const props = withDefaults(
    defineProps<{
        row: Record<string, unknown>;
        /** Draw a checkbox on each schedule line. Ticking it emits `toggle-schedule`. */
        selectable?: boolean;
        /** The schedule ids that are ticked. A ticked line's checkbox stays showing. */
        selectedScheduleIds?: ReadonlySet<number>;
    }>(),
    { selectable: false, selectedScheduleIds: () => new Set<number>() },
);

const emit = defineEmits<{
    /** A schedule's checkbox was clicked. The event carries the shift key, so the page can tick a range. */
    (e: 'toggle-schedule', scheduleId: number, event: MouseEvent): void;
}>();

const entries = useCardSchedules(() => props.row);

/** Schedules exist, but none the index has written in full yet: not "Not scheduled". */
const awaitingIndex = computed(() => {
    const items = props.row.location_zone_custom_playlist_schedules;
    return Array.isArray(items) && items.length > 0 && !items.some(isIndexedSchedule);
});
// The card's own clock, so the schedule's state moves on while the card stays open.
const now = computed(() => scheduleNow());

/** The list's own name and zone name, for the editor's heading. */
const listName = computed(() => (typeof props.row.name === 'string' ? props.row.name : null));
const ownerZoneName = computed(() => {
    const zone = props.row.location_zone as { display_string?: unknown } | null | undefined;
    return typeof zone?.display_string === 'string' ? zone.display_string : null;
});

/**
 * The zone line for the zone that wrote each schedule, by schedule id, when it is not the list's
 * own zone. The inspector groups schedules under a heading for each zone. The card draws that
 * heading inside a line only when another zone wrote it. The list's own zone is already on the
 * card's zone line.
 *
 * It is worked out once per list of entries. Each line's zone line then gets the same object on
 * every redraw, so a tick elsewhere on the page does not redraw it.
 */
const otherZoneRowsByScheduleKey = computed(() => {
    const ownerZoneId = positiveZoneId(props.row.location_zone_id);
    const rows = new Map<string, Record<string, unknown>>();
    for (const entry of entries.value) {
        const zoneId = positiveZoneId(entry.schedule.location_zone_id);
        if (zoneId === null || zoneId === ownerZoneId) continue;
        const zoneRow = zoneLineRowFor(zoneId);
        if (zoneRow !== null) rows.set(entry.key, zoneRow);
    }
    return rows;
});

function scheduleIdOf(entry: ScheduleEntry): number {
    return Number(entry.schedule.id);
}

function isTicked(entry: ScheduleEntry): boolean {
    return props.selectedScheduleIds.has(scheduleIdOf(entry));
}

function onToggle(entry: ScheduleEntry, event: MouseEvent) {
    emit('toggle-schedule', scheduleIdOf(entry), event);
}

/** Ask the list's inspector to open on Schedules and point at this schedule. */
function requestScheduleInList(entry: ScheduleEntry) {
    requestPlaylistContainerSection('LocationZoneCustomPlaylist', props.row.id, 'schedules', entry.schedule.id);
}
</script>

<template>
    <div class="flex min-w-0 flex-col gap-1.5" data-testid="container-schedules-line">
        <template v-if="entries.length > 0">
            <!-- Each line is its own named hover group, so the pointer over one line shows only that line's checkbox.
                 The line under the pointer fills with the card's own hover fill, so the user sees which
                 schedule a click opens. The search card reads data-card-schedule-line to hide the list's
                 checkbox and its own fill meanwhile. The negative margin keeps the text where it was. -->
            <div
                v-for="entry in entries"
                :key="entry.key"
                class="group/schedule-line -mx-1 flex min-w-0 items-start gap-1.5 rounded-md px-1 transition-colors hover:bg-muted/30"
                data-testid="card-schedule-line"
                data-card-schedule-line
                :data-schedule-id="entry.schedule.id"
                @click="requestScheduleInList(entry)"
            >
                <!-- It keeps its space when hidden, so the line never shifts as it appears.
                     A device with no hover, such as a phone, always shows it. -->
                <Checkbox
                    v-if="selectable"
                    class="mt-0.5 shrink-0 transition-opacity"
                    :class="isTicked(entry) ? '' : 'opacity-0 group-hover/schedule-line:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100'"
                    :model-value="isTicked(entry)"
                    :aria-label="`Select schedule ${entry.text}`"
                    data-testid="card-schedule-checkbox"
                    @click.stop="onToggle(entry, $event)"
                    @keydown.stop
                />
                <ScheduleRow class="min-w-0 flex-1" :schedule="entry.schedule" :summary="entry.summary" :now="now" density="card" embedded>
                    <!-- Inside the schedule's colour rail, so the zone reads as part of the schedule. -->
                    <template v-if="otherZoneRowsByScheduleKey.has(entry.key)" #heading>
                        <PlaylistContainerZoneLine :row="otherZoneRowsByScheduleKey.get(entry.key)!" class="min-w-0 text-xs" data-testid="card-schedule-zone" />
                    </template>
                    <template #actions="{ schedule: shownSchedule }">
                        <ScheduleRowActions
                            :schedule="shownSchedule"
                            :list-id="row.id"
                            :list-name="listName"
                            :owner-zone-id="row.location_zone_id"
                            :owner-zone-name="ownerZoneName"
                        />
                    </template>
                </ScheduleRow>
            </div>
        </template>
        <p v-else class="text-muted-foreground text-xs leading-snug" data-testid="container-schedules-text">
            {{ awaitingIndex ? SCHEDULES_UPDATING_TEXT : 'Not scheduled' }}
        </p>
    </div>
</template>
